import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';

type Guest = SpaPackageFormValues['guests'][number];
type SelectionData = NonNullable<NonNullable<Guest['configurationGroups']>[string][number]>;

type UseConfigGroupHandlersParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  configGroupsNoExtras: ConfigurationGroupType[];
};

function getRequiredGroup(configGroupsNoExtras: ConfigurationGroupType[], panelIndex: number) {
  return configGroupsNoExtras.filter((cg) => cg.isRequired)[panelIndex];
}

function updateGuestConfigGroup(
  form: UseFormReturn<SpaPackageFormValues>,
  guestIndex: number,
  groupKey: string,
  updater: (
    currentSelections: (SelectionData | null | undefined)[]
  ) => (SelectionData | null | undefined)[]
) {
  const currentGuests = form.getValues('guests') || [];
  const currentGuest = currentGuests[guestIndex];
  const currentSelections = currentGuest?.configurationGroups?.[groupKey] || [];
  const nextSelections = updater(currentSelections);
  if (nextSelections === currentSelections) return;

  const updatedGuests = currentGuests.map((guest, idx) => {
    if (idx !== guestIndex) return guest;
    const configGroups = guest.configurationGroups || {};
    return {
      ...guest,
      configurationGroups: {
        ...configGroups,
        [groupKey]: nextSelections,
      },
    };
  });
  form.setValue('guests', updatedGuests as SpaPackageFormValues['guests'], {
    shouldValidate: true,
  });
}

function writeTimeslotAt(
  selections: (SelectionData | null | undefined)[],
  group: ConfigurationGroupType,
  optionIdx: number,
  timeslotValue: string,
  ensureSelected: boolean
) {
  const endTime = group.options[optionIdx]?.timeslots?.find(
    (ts) => ts.startTime === timeslotValue
  )?.endTime;
  const timeslot = endTime ? { startTime: timeslotValue, endTime } : null;
  const current = selections[optionIdx];

  if (!current && !ensureSelected) return selections;

  const updated = [...selections];
  updated[optionIdx] = current
    ? {
        configurationGroupId: current.configurationGroupId,
        optionId: current.optionId,
        isSelected: ensureSelected ? true : current.isSelected,
        timeslot,
      }
    : {
        configurationGroupId: group.id,
        optionId: group.options[optionIdx].id,
        isSelected: true,
        timeslot,
      };
  return updated;
}

export function useConfigGroupHandlers({
  form,
  configGroupsNoExtras,
}: UseConfigGroupHandlersParams) {
  const handleAllOptionsTimeslotChange = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string, timeslotValue: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;
      const optionIdx = group.options.findIndex((o) => String(o.id) === optionId);
      if (optionIdx === -1) return;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) =>
        writeTimeslotAt(selections, group, optionIdx, timeslotValue, true)
      );
    },
    [form, configGroupsNoExtras]
  );

  const handleSingleOptionSelect = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;

      updateGuestConfigGroup(form, guestIndex, groupKey, () =>
        group.options.map((opt) => ({
          configurationGroupId: group.id,
          optionId: opt.id,
          isSelected: String(opt.id) === optionId,
          timeslot: null,
        }))
      );
    },
    [form, configGroupsNoExtras]
  );

  const handleSingleOptionTimeslotChange = useCallback(
    (guestIndex: number, panelIndex: number, timeslotValue: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;

      const currentGuests = form.getValues('guests') || [];
      const guest = currentGuests[guestIndex];
      const currentSelections = guest?.configurationGroups?.[groupKey] || [];
      const selectedIdx = currentSelections.findIndex((s) => s?.isSelected);
      if (selectedIdx === -1 || selectedIdx == null) return;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) => {
        const selectedOpt = group.options[selectedIdx];
        const endTime = selectedOpt?.timeslots?.find(
          (ts) => ts.startTime === timeslotValue
        )?.endTime;

        const current = selections[selectedIdx];
        if (!current) return selections;
        const updated = [...selections];
        updated[selectedIdx] = {
          configurationGroupId: current.configurationGroupId,
          optionId: current.optionId,
          isSelected: current.isSelected,
          timeslot: endTime ? { startTime: timeslotValue, endTime } : null,
        };
        return updated;
      });
    },
    [form, configGroupsNoExtras]
  );

  const handleMultiOptionToggle = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;
      const optionIdx = group.options.findIndex((o) => String(o.id) === optionId);
      if (optionIdx === -1) return;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) => {
        const current = selections[optionIdx];
        const willSelect = !current?.isSelected;

        if (willSelect && group.maximumSelections) {
          const selectedCount = selections.filter((s) => s?.isSelected).length;
          if (selectedCount >= group.maximumSelections) return selections;
        }

        const updated = [...selections];
        updated[optionIdx] = {
          configurationGroupId: group.id,
          optionId: group.options[optionIdx].id,
          isSelected: willSelect,
          timeslot: null,
        };
        return updated;
      });
    },
    [form, configGroupsNoExtras]
  );

  const handleMultiOptionTimeslotChange = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string, timeslotValue: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;
      const optionIdx = group.options.findIndex((o) => String(o.id) === optionId);
      if (optionIdx === -1) return;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) =>
        writeTimeslotAt(selections, group, optionIdx, timeslotValue, false)
      );
    },
    [form, configGroupsNoExtras]
  );

  return {
    handleAllOptionsTimeslotChange,
    handleSingleOptionSelect,
    handleSingleOptionTimeslotChange,
    handleMultiOptionToggle,
    handleMultiOptionTimeslotChange,
  };
}
